import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useConfirm } from './ConfirmDialog';
import { Badge, Button, Checkbox, EmptyState, Input, LoadingBlock, useToast } from './ui';

type Fit = 'SAME' | 'YOUNGER' | 'OLDER' | 'OUTSIDE' | 'NO_BIRTH_DATE';

interface Candidate {
  personId: string;
  name: string;
  teamId: string;
  teamName: string;
  jerseyNumber: number | null;
  occasional: boolean;
  birthYear: number | null;
  ageClass: number | null;
  fit: Fit;
  sameDay: string[];
}

interface Candidates {
  teams: { teamId: string; name: string; invited: boolean }[];
  youth: boolean;
  ageClass: number | null;
  rule: { younger: number; older: number } | null;
  candidates: Candidate[];
}

const FIT: Record<Fit, { label: string; tone: 'success' | 'neutral' | 'warning' | 'info' }> = {
  SAME: { label: 'passt', tone: 'success' },
  YOUNGER: { label: 'rückt hoch', tone: 'info' },
  OLDER: { label: 'älter', tone: 'warning' },
  OUTSIDE: { label: 'außerhalb der Regel', tone: 'warning' },
  NO_BIRTH_DATE: { label: 'Geburtsdatum fehlt', tone: 'neutral' },
};

/**
 * Spieler aus anderen Mannschaften für diesen einen Termin dazuholen.
 *
 * Die Vorschläge kommen vom Server: bei Erwachsenen die Mannschaften
 * desselben Bereichs, im Nachwuchs passende Jahrgänge. Termine am selben Tag
 * stehen als Hinweis dabei - gesperrt wird nichts.
 */
export function BorrowPanel({ sessionId, onClose }: { sessionId: string; onClose: () => void }): JSX.Element {
  const toast = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const qc = useQueryClient();
  const [alleJahrgaenge, setAlleJahrgaenge] = useState(false);
  const [suche, setSuche] = useState('');

  const queryKey = ['sports-borrow-candidates', sessionId, alleJahrgaenge];
  const candidates = useQuery({
    queryKey,
    queryFn: () => api.get<Candidates>(`/api/sports/sessions/${sessionId}/borrow-candidates`, alleJahrgaenge ? { all: true } : {}),
  });

  const dazuholen = useMutation({
    mutationFn: (candidate: Candidate) => api.put(`/api/sports/sessions/${sessionId}/borrowed/${candidate.personId}`),
    onSuccess: async (_result, candidate) => {
      await qc.invalidateQueries({ queryKey: ['attendance'] });
      await qc.invalidateQueries({ queryKey: ['sports-borrow-candidates', sessionId] });
      toast.show(`${candidate.name} dazugeholt`);
    },
    onError: (error: unknown) => toast.show(error instanceof ApiError ? error.message : 'Konnte nicht dazugeholt werden', 'error'),
  });

  const einladen = useMutation({
    mutationFn: (input: { teamId: string; name: string; invite: boolean }) => input.invite
      ? api.put(`/api/sports/sessions/${sessionId}/guest-teams/${input.teamId}`)
      : api.del(`/api/sports/sessions/${sessionId}/guest-teams/${input.teamId}`),
    onSuccess: async (_result, input) => {
      await qc.invalidateQueries({ queryKey: ['attendance'] });
      await qc.invalidateQueries({ queryKey: ['sports-borrow-candidates', sessionId] });
      await qc.invalidateQueries({ queryKey: ['sports-sessions'] });
      toast.show(input.invite ? `${input.name} eingeladen` : `Einladung an ${input.name} zurückgenommen`);
    },
    onError: (error: unknown) => toast.show(error instanceof ApiError ? error.message : 'Nicht möglich', 'error'),
  });

  const einladungSchalten = async (team: { teamId: string; name: string; invited: boolean }): Promise<void> => {
    if (team.invited) {
      const ok = await confirm({
        title: `Einladung an ${team.name} zurücknehmen?`,
        message: `Die Spieler von ${team.name} werden aus diesem Termin entfernt, ihre bisherigen Zu- und Absagen für diesen Termin gehen aus der Übersicht verloren.`,
        confirmLabel: 'Ja, zurücknehmen',
        tone: 'danger',
      });
      if (!ok) return;
    }
    einladen.mutate({ teamId: team.teamId, name: team.name, invite: !team.invited });
  };

  const gruppen = useMemo(() => {
    const begriff = suche.trim().toLocaleLowerCase('de-AT');
    const gefiltert = (candidates.data?.candidates ?? []).filter((c) => !begriff || c.name.toLocaleLowerCase('de-AT').includes(begriff));
    const map = new Map<string, { teamName: string; rows: Candidate[] }>();
    for (const candidate of gefiltert) {
      const gruppe = map.get(candidate.teamId) ?? { teamName: candidate.teamName, rows: [] };
      gruppe.rows.push(candidate);
      map.set(candidate.teamId, gruppe);
    }
    return [...map.values()];
  }, [candidates.data, suche]);

  const data = candidates.data;

  return (
    <div className="mb-3 space-y-3 rounded-xl border border-brand-200 bg-brand-50/40 p-3">
      {confirmDialog}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-900">Spieler dazuholen</p>
          <p className="text-xs text-slate-600">
            {data?.youth && data.rule
              ? `Vorschläge nach Jahrgang für U${data.ageClass}: bis ${data.rule.younger} jünger, ${data.rule.older === 0 ? 'keine älteren' : `bis ${data.rule.older} älter`}. Gilt nur für diesen Termin.`
              : 'Aus den anderen Mannschaften desselben Bereichs. Gilt nur für diesen Termin.'}
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}>Schließen</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          aria-label="Kandidaten suchen"
          placeholder="Name suchen …"
          value={suche}
          className="h-8 max-w-xs rounded-lg py-0 text-sm"
          onChange={(event) => setSuche(event.target.value)}
        />
        {data?.youth && (
          <Checkbox label="Alle Jahrgänge zeigen" checked={alleJahrgaenge} onChange={setAlleJahrgaenge} />
        )}
      </div>

      {data && (data.teams?.length ?? 0) > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-white p-2 ring-1 ring-slate-200">
          <span className="text-xs text-slate-600">Ganze Mannschaft einladen (gemeinsames Training):</span>
          {data.teams.map((team) => (
            <Button
              key={team.teamId}
              size="sm"
              variant={team.invited ? 'primary' : 'secondary'}
              aria-pressed={team.invited}
              loading={einladen.isPending && einladen.variables?.teamId === team.teamId}
              onClick={() => void einladungSchalten(team)}
            >
              {team.invited ? `✓ ${team.name} eingeladen` : team.name}
            </Button>
          ))}
        </div>
      )}

      {candidates.isLoading && <LoadingBlock />}
      {candidates.error && <p className="text-sm text-rose-700">Die Vorschläge konnten nicht geladen werden.</p>}
      {data && gruppen.length === 0 && (
        <EmptyState
          title="Keine Vorschläge"
          description={data.youth ? 'Keine passenden Jahrgänge in anderen Nachwuchskadern.' : 'In den anderen Mannschaften dieses Bereichs ist niemand eingetragen.'}
        />
      )}

      {gruppen.map((gruppe) => (
        <div key={gruppe.teamName}>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{gruppe.teamName}</p>
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
            {gruppe.rows.map((candidate) => (
              <li key={candidate.personId} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                <div className="min-w-0 flex-1 basis-48">
                  <p className="truncate text-sm font-medium text-slate-900">
                    {candidate.name}
                    {candidate.jerseyNumber !== null && <span className="ml-1 text-xs font-normal text-slate-500">Nr. {candidate.jerseyNumber}</span>}
                    {candidate.occasional && <span className="ml-1 text-xs font-normal text-slate-500">· Aushilfe</span>}
                  </p>
                  <p className="text-xs text-slate-500">
                    {candidate.birthYear ? `Jg. ${candidate.birthYear}` : ''}
                    {data?.youth && candidate.ageClass !== null ? `${candidate.birthYear ? ' · ' : ''}U${candidate.ageClass}` : ''}
                  </p>
                  {candidate.sameDay.length > 0 && (
                    <p className="text-xs text-amber-700">Am selben Tag: {candidate.sameDay.join(', ')}</p>
                  )}
                </div>
                {data?.youth && <Badge tone={FIT[candidate.fit].tone}>{FIT[candidate.fit].label}</Badge>}
                <Button
                  size="sm"
                  variant="secondary"
                  loading={dazuholen.isPending && dazuholen.variables?.personId === candidate.personId}
                  onClick={() => dazuholen.mutate(candidate)}
                >
                  Dazuholen
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
